import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { getSessions, getGrades, getSubjectsByGrade } from "../../api/attendanceApi";

export default function AttendanceReportPage() {
  const toast = useRef(null);
  const dt = useRef(null);

  const [sessions, setSessions] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [sessionId, setSessionId] = useState(null);
  const [gradeId, setGradeId] = useState(null);
  const [subjectId, setSubjectId] = useState(null);
  const [dateRange, setDateRange] = useState(null); // [start, end]

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    getSessions()
      .then((d) => setSessions(Array.isArray(d) ? d : d.data || []))
      .catch(() => {});
  }, []);

  const handleSessionChange = async (val) => {
    setSessionId(val);
    setGradeId(null);
    setSubjectId(null);
    setGrades([]);
    setSubjects([]);
    if (val) {
      const data = await getGrades(val).catch(() => []);
      setGrades(Array.isArray(data) ? data : data.data || []);
    }
  };

  const handleGradeChange = async (val) => {
    setGradeId(val);
    setSubjectId(null);
    setSubjects([]);
    if (val) {
      const data = await getSubjectsByGrade(val).catch(() => []);
      setSubjects(Array.isArray(data) ? data : data.data || []);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (sessionId) params.sessionId = sessionId;
      if (gradeId) params.gradeId = gradeId;
      if (subjectId) params.subjectId = subjectId;
      if (dateRange?.[0]) params.startDate = dateRange[0].toISOString().slice(0, 10);
      if (dateRange?.[1]) params.endDate = dateRange[1].toISOString().slice(0, 10);

      const res = await axios.get("/api/attendance/report", { params });
      setStudents(res.data.students || []);
      setFetched(true);
    } catch {
      toast.current.show({ severity: "error", summary: "Error", detail: "Failed to fetch report" });
    } finally {
      setLoading(false);
    }
  };

  const percentBody = (row) => {
    const severity = row.percent >= 75 ? "success" : row.percent >= 50 ? "warning" : "danger";
    return <Tag severity={severity} value={`${row.percent}%`} />;
  };

  return (
    <div className="p-4">
      <Toast ref={toast} />

      <div className="mb-4">
        <h2 className="m-0 text-xl font-bold">Attendance Report</h2>
        <p className="m-0 text-color-secondary text-sm mt-1">
          Per-student attendance summary by grade, subject, and date range
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 align-items-end">
        <div className="flex flex-column gap-1">
          <label className="text-sm font-medium">Session</label>
          <Dropdown
            value={sessionId}
            options={sessions.map((s) => ({ label: s.name, value: s.id }))}
            onChange={(e) => handleSessionChange(e.value)}
            placeholder="All Sessions"
            showClear
            style={{ minWidth: "160px" }}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label className="text-sm font-medium">Grade</label>
          <Dropdown
            value={gradeId}
            options={grades.map((g) => ({ label: g.name, value: g.id }))}
            onChange={(e) => handleGradeChange(e.value)}
            placeholder="All Grades"
            showClear
            disabled={!sessionId}
            style={{ minWidth: "140px" }}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label className="text-sm font-medium">Subject</label>
          <Dropdown
            value={subjectId}
            options={subjects.map((s) => ({ label: s.name, value: s.id }))}
            onChange={(e) => setSubjectId(e.value)}
            placeholder="All Subjects"
            showClear
            disabled={!gradeId}
            style={{ minWidth: "140px" }}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label className="text-sm font-medium">Date Range</label>
          <Calendar
            value={dateRange}
            onChange={(e) => setDateRange(e.value)}
            selectionMode="range"
            showIcon
            placeholder="All dates"
            style={{ minWidth: "200px" }}
          />
        </div>
        <div className="flex gap-2">
          <Button label="Generate" icon="pi pi-chart-bar" onClick={fetchReport} loading={loading} />
          {fetched && students.length > 0 && (
            <Button
              label="Export CSV"
              icon="pi pi-download"
              outlined
              onClick={() => dt.current.exportCSV()}
            />
          )}
        </div>
      </div>

      {/* Table */}
      {fetched && (
        <DataTable
          ref={dt}
          value={students}
          loading={loading}
          emptyMessage="No attendance records found for the selected filters."
          stripedRows
          exportFilename="attendance-report"
        >
          <Column field="full_name" header="Student" sortable />
          <Column field="present" header="Present" sortable style={{ width: "6rem", textAlign: "center" }} />
          <Column field="absent" header="Absent" sortable style={{ width: "6rem", textAlign: "center" }} />
          <Column field="late" header="Late" sortable style={{ width: "6rem", textAlign: "center" }} />
          <Column field="total" header="Total Days" sortable style={{ width: "7rem", textAlign: "center" }} />
          <Column header="Attendance %" body={percentBody} sortField="percent" sortable style={{ width: "9rem" }} />
        </DataTable>
      )}
    </div>
  );
}
